import React from 'react';
import { useNavigate } from 'react-router-dom';
import './BandInvitation.css';

const BandInvitation = () => {
  const navigate = useNavigate();

  const handleJoinNow = () => {
    // For now, navigate to auth since band profile page doesn't exist yet
    navigate('/auth');
  };

  return (
    <div className="band-invitation-container">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">Join BagpipeBlasphemy as a Band</h1>
          <p className="hero-subtitle">Where Technology Meets Tradition</p>
          <div className="hero-description">
            <p>
              Welcome to BagpipeBlasphemy, the platform uniting the global bagpipe community with cutting-edge technology. 
              We're inviting pipe bands from every corner of the world—whether you're a championship-grade band in Glasgow, 
              a community ensemble in Melbourne, or a youth group in Chicago—to join our vibrant network. By registering, 
              your band can gain visibility, attract new members, and harness our innovative AI sheet music generator to 
              elevate rehearsals and performances. Be part of a community that celebrates the tradition of bagpiping while 
              embracing the future.
            </p>
          </div>
          <button className="cta-button primary" onClick={handleJoinNow}>
            Join Now
          </button>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="why-join-section">
        <div className="section-content">
          <h2 className="section-title">Why Join BagpipeBlasphemy?</h2>
          
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">🌟</div>
              <h3>Boost Your Band's Visibility</h3>
              <ul>
                <li>Showcase your band to a global audience of pipers, drummers, and fans.</li>
                <li>Attract new members, from beginners to seasoned players, through our intuitive search tools.</li>
                <li>Promote your performances, workshops, and events to a worldwide community.</li>
              </ul>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">👥</div>
              <h3>Grow Your Membership</h3>
              <ul>
                <li>Connect with students and musicians eager to join a pipe band, whether for competitions, parades, or community events.</li>
                <li>Highlight your band's unique culture, competitive achievements, or welcoming vibe to draw the right talent.</li>
                <li>Reach learners and players in your area or online, expanding your roster.</li>
              </ul>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">🤖</div>
              <h3>Elevate Rehearsals with AI Technology</h3>
              <ul>
                <li>Access our AI sheet music generator to create custom tunes, exercises, or arrangements tailored to your band's needs.</li>
                <li>Streamline rehearsal planning with on-demand sheet music that blends traditional styles with modern innovation.</li>
                <li>Equip your band with tools that save time and inspire creativity, keeping tradition alive in new ways.</li>
              </ul>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">💰</div>
              <h3>Free and Easy to Join</h3>
              <ul>
                <li>Listing your band is free during our launch phase—no upfront costs.</li>
                <li>Customize your profile to reflect your band's identity and goals.</li>
                <li>Manage inquiries and communications effortlessly through our platform.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>



      {/* How to Get Started Section */}
      <section className="getting-started-section">
        <div className="section-content">
          <h2 className="section-title">How to Get Started</h2>
          
          <div className="steps-container">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3>Sign Up</h3>
              <p>Create your free band account in just a few minutes.</p>
            </div>

            <div className="step-card">
              <div className="step-number">2</div>
              <h3>Build Your Profile</h3>
              <p>Add details about your band, including location, membership needs, and performance highlights.</p>
            </div>

            <div className="step-card">
              <div className="step-number">3</div>
              <h3>Engage with the Community</h3>
              <p>Start connecting with potential members and fans eager to support your band.</p>
            </div>
          </div>

          <div className="cta-container">
            <button className="cta-button primary large" onClick={handleJoinNow}>
              Join Now
            </button>
          </div>
        </div>
      </section>

      {/* Power of Pipe Bands Section */}
      <section className="power-section">
        <div className="section-content">
          <h2 className="section-title">The Power of Pipe Bands</h2>
          <div className="power-content">
            <p>
              Pipe bands are the heartbeat of the bagpipe tradition, bringing communities together through the stirring 
              sounds of pipes and drums. From Highland games to local parades, your band carries forward a legacy of music, 
              camaraderie, and pride. At BagpipeBlasphemy, we're passionate about amplifying your impact by connecting you 
              with new members and providing tools to enhance your music. Join us to keep the tradition thriving in the modern age.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="section-content">
          <h2 className="section-title">Frequently Asked Questions</h2>
          
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Is there a cost to join?</h3>
              <p>
                No! Listing your band is free during our launch phase. There are no upfront fees, and you control how you engage with the platform.
              </p>
            </div>

            <div className="faq-item">
              <h3>Do we need to recruit members?</h3>
              <p>
                Not at all. You can join to promote your performances, share your band's story, or simply connect with the bagpipe community. Recruiting is optional.
              </p>
            </div>

            <div className="faq-item">
              <h3>How does the AI sheet music generator work?</h3>
              <p>
                Our AI creates custom bagpipe sheet music based on your specifications, ideal for new arrangements, practice tunes, 
                or competition pieces. As a band, you'll have access to this tool to enhance rehearsals.
              </p>
            </div>

            <div className="faq-item">
              <h3>How do people find our band?</h3>
              <p>
                Visitors can search for bands by location, competitive level, or membership openings. Your profile will be visible 
                globally, and we'll highlight active bands to boost your exposure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="section-content">
          <h2 className="section-title">Ready to Amplify Your Band's Legacy?</h2>
          <p className="final-cta-text">
            Join BagpipeBlasphemy today and become part of a global community dedicated to the art of bagpiping. 
            Whether you're a competitive powerhouse or a community ensemble, your band belongs here.
          </p>
          <button className="cta-button primary large" onClick={handleJoinNow}>
            Sign Up Now
          </button>
        </div>
      </section>
    </div>
  );
};

export default BandInvitation; 